import { sql } from "drizzle-orm";
import { boolean, index, integer, jsonb, pgTable, text, timestamp, varchar, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// User storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  passwordHash: varchar("password_hash"),
  isPasswordUser: varchar("is_password_user").default("false"),
  // 'internal' = full workspace member (Sales Rep / Artist / Billing / Admin / Marketing).
  // 'external' = outside collaborator granted access via module_grants. Externals see only
  // the modules/resources they hold an active grant for; they cannot access any other part
  // of the CRM. See `module_grants` in shared/schema.ts.
  userType: varchar("user_type", { length: 20 }).notNull().default("internal"),
  // ── MFA (multi-factor auth) — per-user enrollment state ──────────────────
  // mfaEnabled: at least one second factor is verified and active.
  mfaEnabled: boolean("mfa_enabled").notNull().default(false),
  // mfaEnrolledAt: when the *first* second factor was confirmed.
  mfaEnrolledAt: timestamp("mfa_enrolled_at"),
  // mfaLastVerifiedAt: most recent successful 2nd-factor verification (used to
  // expire MFA sessions according to workspace policy).
  mfaLastVerifiedAt: timestamp("mfa_last_verified_at"),
  // TOTP secret, AES-256-GCM encrypted via the same field-encryption module
  // used for PII columns. NULL until TOTP is enrolled.
  mfaTotpSecretEncrypted: text("mfa_totp_secret_encrypted"),
  // SMS factor — verified phone number in E.164 (+15555550123).
  mfaPhoneE164: varchar("mfa_phone_e164", { length: 32 }),
  mfaPhoneVerifiedAt: timestamp("mfa_phone_verified_at"),
  // Email factor uses the user's primary email — no separate column needed,
  // but we track when it was verified for the MFA purpose.
  mfaEmailVerifiedAt: timestamp("mfa_email_verified_at"),
  // 10 single-use bcrypt-hashed recovery codes. JSON: { code: string, usedAt: string|null }[].
  mfaBackupCodesHashedJson: jsonb("mfa_backup_codes_hashed_json"),
  // 'totp' | 'email' | 'sms' | 'passkey' — which factor to default to on the
  // challenge screen. NULL = pick the strongest enrolled.
  mfaDefaultMethod: varchar("mfa_default_method", { length: 16 }),
  // Admin can mark a user inactive to (a) block them from logging in and
  // (b) hide them from assignment selectors, goals, activities, and other
  // "pick a user" surfaces — while still preserving the historic records
  // they own (lead.rep, invoice.rep, booking.userId, etc.). Toggle lives
  // in Admin Panel → Users & Permissions. See migration 0079.
  isActive: boolean("is_active").notNull().default(true),
  deactivatedAt: timestamp("deactivated_at"),
  // Task #190 — first-login password reset flow. Set to true for every
  // admin-created user so they are forced to choose a new password on
  // their very first sign-in. Never set for self-serve (OAuth/Signup) users.
  mustResetPassword: boolean("must_reset_password").notNull().default(false),
  // Task #561 — Email verification. Set when the user confirms their address
  // via the one-time link sent after signup. NULL for unverified accounts and
  // for Replit-OIDC users (who are verified upstream). Accounts created before
  // the verification feature was deployed are grandfathered as verified by the
  // server guard logic (createdAt < EMAIL_VERIFICATION_CUTOFF_DATE).
  emailVerifiedAt: timestamp("email_verified_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

// Schema for updating user profile info
export const updateUserSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(100).optional(),
  lastName: z.string().min(1, "Last name is required").max(100).optional(),
  email: z.string().email("Invalid email address").optional(),
});

export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ── MFA: Passkeys (WebAuthn) ────────────────────────────────────────────────
// One row per registered authenticator (Touch ID, Windows Hello, security key,
// platform passkey, etc.). A user may have many passkeys.
export const userMfaPasskeys = pgTable(
  "user_mfa_passkeys",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // WebAuthn credential ID (base64url). Unique across all users.
    credentialId: text("credential_id").notNull(),
    // Public key, base64url. Verified against during authentication.
    publicKey: text("public_key").notNull(),
    // Signature counter — bumped on each auth; helps detect cloned authenticators.
    counter: integer("counter").notNull().default(0),
    // Comma-separated transport hints from the browser (usb, nfc, ble, internal, hybrid).
    transports: text("transports"),
    // User-chosen label ("MacBook Touch ID", "YubiKey 5C", "iPhone").
    deviceName: varchar("device_name", { length: 120 }),
    // 'platform' (built into device) or 'cross-platform' (USB key etc.) — used for
    // surfacing strength hints in the UI. May be null for older registrations.
    aaguid: varchar("aaguid", { length: 64 }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    lastUsedAt: timestamp("last_used_at"),
  },
  (table) => [
    uniqueIndex("ux_user_mfa_passkeys_credential_id").on(table.credentialId),
    index("ix_user_mfa_passkeys_user").on(table.userId),
  ]
);

// ── Self-serve password reset tokens (Task #219) ────────────────────────────
// One row per "Forgot password?" request. We store only a SHA-256 hash of the
// raw token (never the token itself) so a DB leak can't be replayed to reset an
// account. Tokens are single-use (usedAt) and short-lived (expiresAt, ~1h).
// Outstanding tokens for a user are invalidated when a new one is issued and
// when a reset completes. Rescues users stuck in the admin-driven
// mustResetPassword=true state since completion clears that flag too.
export const passwordResetTokens = pgTable(
  "password_reset_tokens",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // SHA-256 hex of the raw token. Unique so a lookup is a single index hit.
    tokenHash: text("token_hash").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    usedAt: timestamp("used_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("ux_password_reset_tokens_hash").on(table.tokenHash),
    index("ix_password_reset_tokens_user").on(table.userId),
    index("ix_password_reset_tokens_expires").on(table.expiresAt),
  ]
);

export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// ── Email verification tokens (Task #561) ───────────────────────────────────
// One row per verification request. We store only a SHA-256 hash of the raw
// token — never the raw token itself. Tokens are single-use (usedAt) and
// expire after 24 hours. A new token invalidates outstanding ones for the same
// user (we mark them used before issuing the new one).
export const emailVerificationTokens = pgTable(
  "email_verification_tokens",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // SHA-256 hex of the raw token. Unique so a lookup is a single index hit.
    tokenHash: text("token_hash").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    usedAt: timestamp("used_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("ux_email_verification_tokens_hash").on(table.tokenHash),
    index("ix_email_verification_tokens_user").on(table.userId),
    index("ix_email_verification_tokens_expires").on(table.expiresAt),
  ]
);

export type EmailVerificationToken = typeof emailVerificationTokens.$inferSelect;

export const insertUserMfaPasskeySchema = createInsertSchema(userMfaPasskeys).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
});
export type InsertUserMfaPasskey = z.infer<typeof insertUserMfaPasskeySchema>;
export type UserMfaPasskey = typeof userMfaPasskeys.$inferSelect;

// ── MFA: Challenges (email/SMS one-time codes) ──────────────────────────────
// Short-lived rows. Each issuance is hashed with bcrypt; verifying replays the
// hash. Attempts are capped to defeat brute force.
export const userMfaChallenges = pgTable(
  "user_mfa_challenges",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    // 'email' | 'sms' | 'enroll_email' | 'enroll_sms'
    purpose: varchar("purpose", { length: 32 }).notNull(),
    codeHash: text("code_hash").notNull(),
    // For 'sms' / 'enroll_sms', the destination phone (E.164). For email, NULL.
    destination: varchar("destination", { length: 255 }),
    attempts: integer("attempts").notNull().default(0),
    expiresAt: timestamp("expires_at").notNull(),
    consumedAt: timestamp("consumed_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    index("ix_user_mfa_challenges_user_purpose").on(table.userId, table.purpose),
    index("ix_user_mfa_challenges_expires").on(table.expiresAt),
  ]
);

export const insertUserMfaChallengeSchema = createInsertSchema(userMfaChallenges).omit({
  id: true,
  createdAt: true,
  consumedAt: true,
  attempts: true,
});
export type InsertUserMfaChallenge = z.infer<typeof insertUserMfaChallengeSchema>;
export type UserMfaChallenge = typeof userMfaChallenges.$inferSelect;

// ── MFA: Trusted Devices ────────────────────────────────────────────────────
// Schema-only in Phase 1. Phase 3 adds "remember this device for 30 days" so
// users with MFA enabled don't get prompted on every login from the same
// browser. The fingerprint is a server-signed cookie hash, never the raw value.
export const userTrustedDevices = pgTable(
  "user_trusted_devices",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
    fingerprintHash: text("fingerprint_hash").notNull(),
    label: varchar("label", { length: 120 }),
    lastSeenIp: varchar("last_seen_ip", { length: 64 }),
    lastSeenUserAgent: text("last_seen_user_agent"),
    lastSeenAt: timestamp("last_seen_at"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("ux_user_trusted_devices_fp").on(table.userId, table.fingerprintHash),
    index("ix_user_trusted_devices_expires").on(table.expiresAt),
  ]
);

export const insertUserTrustedDeviceSchema = createInsertSchema(userTrustedDevices).omit({
  id: true,
  createdAt: true,
  lastSeenAt: true,
});
export type InsertUserTrustedDevice = z.infer<typeof insertUserTrustedDeviceSchema>;
export type UserTrustedDevice = typeof userTrustedDevices.$inferSelect;
